import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Search } from 'lucide-react';
import { usePageConfig } from '../../contexts/PageConfigContext';
import { Permissions } from '../../constants/permissions';
import { routesApi, RouteDto, CreateRouteDto, UpdateRouteDto } from '../../services/api';
import { usePermission } from '../../hooks/usePermission';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Card } from '../../components/ui/card';

export const RoutesPage: React.FC = () => {
  const { setPageConfig } = usePageConfig();
  const canView = usePermission(Permissions.Logistics.ViewRoutes);
  const canCreate = usePermission(Permissions.Logistics.CreateRoutes);
  const canEdit = usePermission(Permissions.Logistics.EditRoutes);
  const canDelete = usePermission(Permissions.Logistics.DeleteRoutes);
  
  const [routes, setRoutes] = useState<RouteDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  
  // Form Dialog
  const [showForm, setShowForm] = useState(false);
  const [editingRoute, setEditingRoute] = useState<RouteDto | null>(null);
  const [formData, setFormData] = useState<CreateRouteDto & { isActive?: boolean }>({
    name: '',
    code: '',
    description: '',
  });

  useEffect(() => {
    setPageConfig({
      title: 'Rutas',
      actions: canCreate ? [
        {
          label: 'Nueva Ruta',
          icon: Plus,
          onClick: () => {
            setEditingRoute(null);
            setFormData({ name: '', code: '', description: '' });
            setShowForm(true);
          },
          variant: 'primary' as const
        }
      ] : []
    });
  }, [setPageConfig, canCreate]);

  const loadRoutes = async () => {
    if (!canView) return;
    try {
      setLoading(true);
      const data = await routesApi.getAll({ isActive: showInactive ? undefined : true });
      setRoutes(data);
    } catch (error) {
      alert('Error al cargar rutas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoutes();
  }, [showInactive]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingRoute) {
        await routesApi.update(editingRoute.id, formData as UpdateRouteDto);
        alert('Ruta actualizada');
      } else {
        await routesApi.create(formData);
        alert('Ruta creada');
      }
      setShowForm(false);
      loadRoutes();
    } catch (error: any) {
      alert(error.message || 'Error al guardar ruta');
    }
  };

  const handleEdit = (route: RouteDto) => {
    setEditingRoute(route);
    setFormData({
      name: route.name,
      code: route.code,
      description: route.description || '',
      isActive: route.isActive
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Eliminar esta ruta?')) return;
    try {
      await routesApi.delete(id);
      alert('Ruta eliminada');
      loadRoutes();
    } catch (error: any) {
      alert(error.message || 'Error al eliminar');
    }
  };

  const filteredRoutes = routes.filter(r => 
    r.name.toLowerCase().includes(search.toLowerCase()) ||
    r.code.toLowerCase().includes(search.toLowerCase())
  );

  if (!canView) return <div className="p-6">No tienes permisos para ver rutas</div>;

  return (
    <div className="p-6 space-y-4">
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nombre o código..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <input
            id="show-inactive"
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="h-4 w-4"
          />
          <Label htmlFor="show-inactive">Mostrar inactivas</Label>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">Cargando...</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredRoutes.map((route) => (
            <Card key={route.id} className="p-4">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold">{route.name}</h3>
                  <p className="text-sm text-muted-foreground">Código: {route.code}</p>
                </div>
                <span className={`px-2 py-1 text-xs rounded ${route.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                  {route.isActive ? 'Activa' : 'Inactiva'}
                </span>
              </div>
              {route.description && (
                <p className="text-sm mb-3">{route.description}</p>
              )}
              <div className="flex gap-2 justify-end">
                {canEdit && (
                  <Button variant="outline" size="sm" onClick={() => handleEdit(route)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
                {canDelete && (
                  <Button variant="destructive" size="sm" onClick={() => handleDelete(route.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>{editingRoute ? 'Editar Ruta' : 'Nueva Ruta'}</DialogTitle>
              <DialogDescription>
                Complete los datos de la ruta
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="name">Nombre *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="code">Código *</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="description">Descripción</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
              {editingRoute && (
                <div className="flex items-center gap-2">
                  <input
                    id="isActive"
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="isActive">Activa</Label>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancelar
              </Button>
              <Button type="submit">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
